import React, { useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import useSWR from 'swr';
import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface PurchaseRequestData {
    key: React.Key;
    id: number;
    pr_no: string;
    pr_date_formatted: string;
    item_group?: string;
    order_type?: string;
    reference_no?: string;
    expected_arrival?: string;
    po_no?: string;
    dealer_rep_code?: string;
    status: string | number;
    set_order: string;
    back_order: string;
}

type DataIndex = keyof PurchaseRequestData;
const fetcher = (url: string) => axios.get(url).then(res => res.data);
const withBrowserPurchaseRequestLogic = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
    return (props: P) => {
        const { userData } = useAuth();
        const [searchText, setSearchText] = useState('');
        const [searchedColumn, setSearchedColumn] = useState('');
        const searchInput = useRef<InputRef>(null);
        const navigate = useNavigate();
        const [currentPage, setCurrentPage] = useState<number>(1);
        const [pageSize, setPageSize] = useState<number>(10);
        const [showAllWarehouse, setShowAllWarehouse] = useState<boolean>(false);
        const hasOtoritas77 = userData?.otoritas ? userData.otoritas.split(';').map((v: string) => v.trim()).includes('77') : false;
        const warehouseFilter = (!showAllWarehouse && userData?.warehouse_code) ? `&dealer_rep_code=${userData.warehouse_code}` : '';
        const { data: responseData, error, isLoading } = useSWR<{ data: PurchaseRequestData[]; total: number }>(
            `${process.env.REACT_APP_BACKEND_URL}/api/purchase-requests?page=${currentPage}&pageSize=${pageSize}${warehouseFilter}`,
            fetcher
        );

        const handleSearch = (
            selectedKeys: string[],
            confirm: FilterDropdownProps['confirm'],
            dataIndex: DataIndex,
        ) => {
            confirm();
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
        };

        const handleReset = (clearFilters: () => void) => {
            clearFilters();
            setSearchText('');
            setSearchedColumn('');
        };

        const handleRowDoubleClick = (record: PurchaseRequestData) => {
            console.log("Double clicked:", record);
            if (record.id) {

                navigate(`/dashboard/purchase_request/edit/${record.id}`);
            } else {
                console.error("Record ID is missing, cannot navigate to edit.");
            }
        };

        const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<PurchaseRequestData> => ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        ref={searchInput}
                        placeholder={`Cari ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Cari
                        </Button>
                        <Button
                            onClick={() => clearFilters && handleReset(clearFilters)}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                confirm({ closeDropdown: false });
                                setSearchText((selectedKeys as string[])[0]);
                                setSearchedColumn(dataIndex);
                            }}
                        >
                            Filter
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                close();
                            }}
                        >
                            Tutup
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
            ),
            onFilter: (value, record) => {

                let recordValue = record[dataIndex];
                return recordValue != null
                    ? recordValue.toString().toLowerCase().includes((value as string).toLowerCase())
                    : false;
            },
            filterDropdownProps: {
                onOpenChange: (visible) => {
                    if (visible) {
                        setTimeout(() => searchInput.current?.select(), 100);
                    }
                },
            },
            render: (text) =>
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                ),
        });

        const getStatusDisplayValue = (status: string | number | null | undefined): string => {
            if (status === null || status === undefined) {
                return '-';
            }
            if (status == 1) return 'Draft';
            if (status == 7) return 'Ready';
            if (status == 6) return 'Closed';
            if (status == 5) return 'Canceled';
            return status.toString();
        };

        const columns: TableColumnsType<PurchaseRequestData> = [
            {
                title: 'PR NO',
                dataIndex: 'pr_no',
                key: 'pr_no',
                ...getColumnSearchProps('pr_no'),
            },
            {
                title: 'PR Date',
                dataIndex: 'pr_date_formatted',
                key: 'pr_date_formatted',
                sorter: (a, b) => dayjs(a.pr_date_formatted, 'DD/MM/YYYY').unix() - dayjs(b.pr_date_formatted, 'DD/MM/YYYY').unix(),
                sortDirections: ['descend', 'ascend'],
            },
            {
                title: 'Item Group',
                dataIndex: 'item_group',
                key: 'item_group',
                ...getColumnSearchProps('item_group'),
            },
            {
                title: 'Order Type',
                dataIndex: 'order_type',
                key: 'order_type',
                ...getColumnSearchProps('order_type'),
            },
            {
                title: 'Arrival Date',
                dataIndex: 'expected_arrival',
                key: 'expected_arrival',
                sorter: (a, b) => dayjs(a.expected_arrival, 'DD/MM/YYYY HH:mm').unix() - dayjs(b.expected_arrival, 'DD/MM/YYYY HH:mm').unix(),
                sortDirections: ['descend', 'ascend'],
            },
            {
                title: 'PO No',
                dataIndex: 'po_no',
                key: 'po_no',
                ...getColumnSearchProps('po_no'),
            },
            {
                title: 'Warehouse',
                dataIndex: 'dealer_rep_code',
                key: 'dealer_rep_code',
                ...getColumnSearchProps('dealer_rep_code'),
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                filterDropdown: getColumnSearchProps('status').filterDropdown,
                filterIcon: getColumnSearchProps('status').filterIcon,
                filterDropdownProps: getColumnSearchProps('status').filterDropdownProps,
                onFilter: (value, record) => {
                    const displayValue = getStatusDisplayValue(record.status);
                    if (!displayValue || displayValue === '-') return false;
                    return displayValue.toLowerCase().includes((value as string).toLowerCase());
                },
                render: (status) => {
                    const displayValue = getStatusDisplayValue(status);
                    return searchedColumn === 'status' ? (
                        <Highlighter
                            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                            searchWords={[searchText]}
                            autoEscape
                            textToHighlight={displayValue}
                        />
                    ) : (
                        displayValue
                    );
                },
            },
            {
                title: 'Set Order',
                dataIndex: 'set_order',
                key: 'set_order',
                hidden: true,
                ...getColumnSearchProps('set_order'),
            },
            {
                title: 'Back Order',
                dataIndex: 'back_order',
                key: 'back_order',
                hidden: true,
                ...getColumnSearchProps('back_order'),
            },
        ];

        const pagination = {
            current: currentPage,
            pageSize: pageSize,
            total: responseData?.total || 0,
            showSizeChanger: true,
            onChange: (page: number, size: number) => {
                setCurrentPage(page);
                setPageSize(size);
            },
        };

        if (error) return <div>Gagal memuat data...</div>;
        return (
            <WrappedComponent
                {...props}
                columns={columns}
                data={responseData?.data}
                isLoading={isLoading}
                pagination={pagination}
                showAllWarehouse={showAllWarehouse}
                setShowAllWarehouse={setShowAllWarehouse}
                hasOtoritas77={hasOtoritas77}
                onRow={(record: PurchaseRequestData) => ({
                    onDoubleClick: () => handleRowDoubleClick(record),
                })}
            />
        );
    };
};

export default withBrowserPurchaseRequestLogic;